import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box } from "@mui/material";
import { ParagraphText } from "../../../../Libraries";
import TabAutoComplete from "../../../../Libraries/TabAutoComplete";
import { BarcodeIconNew, BillPlaceholderIcon } from "../../../../assets/icons/icon";
import { analytics, triggerLogEvent } from "../../../../config/firebase";
import SelectItemModal from "../../../common/Modals/SelectItemModal";
import formStyles from "../../SaleExpense/SaleExpenseForm/styles.module.css";
import styles from "./BillPayments.module.css";
import axios from "axios";
import moment from "moment";
import { sentenceCase } from "../../../../constants";

const BillPaymentForm = ({
  I18n,
  popularBills = [],
  popularBillsLoading = false,
  categoryOptions = [],
  companyOptions = [],
  allBillsFlat = [],
  state,
  setState,
  onCategoryChange,
  onPopularBillSelect,
  onSearchCustomerBill,
  navigate,
  companyIcon = "",
  isFormDisabled = true,
  onEnterPress,
}) => {
  const [showAllBillsModal, setShowAllBillsModal] = useState(false);
  const [consumerSuggestions, setConsumerSuggestions] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  // Tracks logo URLs that failed to load, keyed per item, so those items
  // fall back to BillPlaceholderIcon instead of a broken image.
  const [failedIcons, setFailedIcons] = useState({});
  const markIconFailed = (key) =>
    setFailedIcons((prev) => (prev[key] ? prev : { ...prev, [key]: true }));

  const debounceTimer = useRef(null);
  const cancelTokenRef = useRef(null);

  const cancelSearch = () => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    if (cancelTokenRef.current) cancelTokenRef.current.cancel();
    setConsumerSuggestions([]);
    setIsSearching(false);
  };

  const displayedPopular = (popularBills || []).slice(0, 3);
  const showPopularSkeleton = popularBillsLoading && popularBills.length === 0;
  const showPopularSection = showPopularSkeleton || displayedPopular.length > 0;

  const allBillItems = useMemo(
    () =>
      allBillsFlat.map((b) => {
        const iconKey = `modal_${b.id}`;
        return {
          label: b.name || "",
          img:
            b.icon && !failedIcons[iconKey] ? (
              <img
                src={b.icon}
                alt={b.name}
                width={28}
                height={28}
                style={{ objectFit: "contain" }}
                onError={() => markIconFailed(iconKey)}
              />
            ) : (
              <BillPlaceholderIcon width={19} height={19} />
            ),
          icon: b.icon,
          id: b.id,
          category: b.category,
          businessList: b.businessList,
        };
      }),
    [allBillsFlat, failedIcons],
  );

  const consumerSuggestionList = useMemo(
    () =>
      consumerSuggestions.map((item, index) => {
        const iconSrc = item.icon || companyIcon || "";
        const iconKey = `consumer_${item.bill_consumer_number || index}`;
        return {
          label: item.bill_consumer_number || "",
          value: item.bill_consumer_number || "",
          subLabel: item.created
            ? `${I18n.last_paid}: ${moment(item.created).format("MMMM D")}`
            : "",
          img:
            iconSrc && !failedIcons[iconKey] ? (
              <img
                src={iconSrc}
                alt={item.bill_consumer_number || ""}
                width={28}
                height={28}
                style={{ objectFit: "contain" }}
                onError={() => markIconFailed(iconKey)}
              />
            ) : (
              <Box display="flex" alignItems="center" justifyContent="center" width="100%" height="100%">
                <BillPlaceholderIcon width={19} height={19} />
              </Box>
            ),
          raw: item,
        };
      }),
    [consumerSuggestions, companyIcon, I18n.last_paid, failedIcons],
  );

  const handleBillSelect = (bill) => {
    if (bill.name === state.billingCompany) return;
    cancelSearch();
    onPopularBillSelect(bill);
  };

  const handleModalSelect = (item) => {
    handleBillSelect({
      name: item.label,
      icon: item.icon,
      id: item.id,
      category: item.category,
      businessList: item.businessList,
    });
  };

  const handleConsumerNumberChange = (rawValue) => {
    const value = (rawValue || "").replace(/[^0-9]/g, "");
    setState((e) => ({ ...e, billingConsumerNumber: value }));

    if (value?.length === 1)
      triggerLogEvent(analytics, "w_earn_bill_acc_number");

    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    if (cancelTokenRef.current) cancelTokenRef.current.cancel();

    if (value.length < 5 || !state.billingCompany) {
      setConsumerSuggestions([]);
      setIsSearching(false);
      return;
    }

    debounceTimer.current = setTimeout(async () => {
      setIsSearching(true);
      cancelTokenRef.current = axios.CancelToken.source();
      try {
        const response = await onSearchCustomerBill({
          consumer_number: value,
          billing_company: state.billingCompany,
          cancelToken: cancelTokenRef.current,
          navigate,
        });
        setIsSearching(false);
        if (response?.bills?.length) {
          setConsumerSuggestions(response.bills);
        } else {
          setConsumerSuggestions([]);
        }
      } catch (err) {
        if (!axios.isCancel(err)) {
          setIsSearching(false);
          setConsumerSuggestions([]);
        }
        // cancelled = new request is already in flight, keep skeleton showing
      }
    }, 300);
  };

  // react-barcode-reader silently drops keypresses when any input is focused.
  // This custom listener uses the same timing-based detection but without that gate.
  const handleBarcodeScanRef = useRef(null);
  handleBarcodeScanRef.current = (data) => {
    if (!data || !state.billingCompany) return;
    handleConsumerNumberChange(String(data));
  };

  useEffect(() => {
    const buffer = { chars: "", firstTime: 0, lastTime: 0 };

    const onKeyDown = (e) => {
      const now = Date.now();

      if (e.key === "Enter" || e.keyCode === 13) {
        const { chars, firstTime } = buffer;
        if (chars.length >= 6 && firstTime) {
          const avgMs = (now - firstTime) / chars.length;
          if (avgMs < 35) {
            e.preventDefault();
            handleBarcodeScanRef.current?.(chars);
          }
        }
        buffer.chars = "";
        buffer.firstTime = 0;
        buffer.lastTime = 0;
        return;
      }

      if (e.key.length === 1) {
        const gap = buffer.lastTime ? now - buffer.lastTime : 0;
        if (buffer.lastTime && gap > 50) {
          buffer.chars = "";
          buffer.firstTime = 0;
        }
        if (!buffer.firstTime) buffer.firstTime = now;
        buffer.chars += e.key;
        buffer.lastTime = now;
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);

  const handleSuggestionSelect = (item) => {
    setState((e) => ({
      ...e,
      billingConsumerNumber: item.bill_consumer_number || item.value || "",
    }));
    setIsSearching(false);
    setConsumerSuggestions([]);
  };

  useEffect(() => {
    return () => cancelSearch();
  }, []);

  return (
    <Box>
      {showPopularSection && (
        <Box className={styles.popularSection}>
          <Box className={styles.popularHeader}>
            <ParagraphText
              className={`fontSize14 newFontColor ${styles.popularLabel}`}
            >
              {sentenceCase(I18n.popular)}
            </ParagraphText>
            {!showPopularSkeleton && (
              <ParagraphText
                id="bill_payment_see_more_link"
                className={styles.seeMoreLink}
                onClick={() => setShowAllBillsModal(true)}
              >
                {I18n.see_more}
              </ParagraphText>
            )}
          </Box>
          <Box className={styles.popularRow}>
            {showPopularSkeleton
              ? [0, 1, 2].map((i) => (
                  <Box key={i} className={styles.popularCardSkeleton} />
                ))
              : displayedPopular.map((bill, i) => (
                  <Box
                    key={i}
                    id={`bill_payment_popular_card_${bill.id ?? i}`}
                    className={`${styles.popularCard} ${styles[`popularCardBg${i}`] || ""} ${state.billingCompany === bill.name ? styles.popularCardActive : ""}`}
                    onClick={() => {
                      triggerLogEvent(analytics, "w_earn_bill_popular");
                      handleBillSelect(bill);
                    }}
                  >
                    {bill.icon && !failedIcons[`popular_${bill.id ?? i}`] ? (
                      <img
                        src={bill.icon}
                        alt={bill.name}
                        className={styles.popularIcon}
                        onError={() => markIconFailed(`popular_${bill.id ?? i}`)}
                      />
                    ) : (
                      <BillPlaceholderIcon width={20} height={20} />
                    )}
                    <ParagraphText
                      className={`fontSize14 ${styles.popularName}`}
                    >
                      {bill.name}
                    </ParagraphText>
                  </Box>
                ))}
          </Box>
        </Box>
      )}

      <Box className={styles.reviewFields}>
        <Box className={`${formStyles.tabAutoCompleteBox} tabAutoCompleteBox`}>
          <TabAutoComplete
            id="bill_payment_category"
            list={categoryOptions}
            label={I18n.category}
            handleSelect={(item) => {
              if (!item) {
                cancelSearch();
                onCategoryChange("");
                return;
              }
              if (item.name === state.billingCategory) return;
              cancelSearch();
              triggerLogEvent(analytics, "w_earn_bill_category");
              onCategoryChange(item.name);
            }}
            value={state.billingCategory || ""}
            placeholder={I18n.select_bill_category}
            required
            handleStartLogEvent={() => {}}
            handleManuallyInput={() => {}}
            manuallyFocusInput={false}
          />
        </Box>

        {state.billingCategory ? (
          <Box
            className={`${formStyles.tabAutoCompleteBox} tabAutoCompleteBox`}
          >
            <TabAutoComplete
              id="bill_payment_company"
              list={companyOptions}
              label={I18n.company}
              handleSelect={(item) => {
                if (!item) {
                  cancelSearch();
                  setState((e) => ({
                    ...e,
                    billingCompany: "",
                    billingCompanyId: "",
                    billingConsumerNumber: "",
                  }));
                  return;
                }
                // Same company re-selected — nothing changed, don't wipe the consumer number
                if ((item.name || item.value) === state.billingCompany) return;
                cancelSearch();
                triggerLogEvent(analytics, "w_earn_bill_company");
                setState((e) => ({
                  ...e,
                  billingCompany: item.name || item.value || "",
                  billingCompanyId: item.id || "",
                  billingConsumerNumber: "",
                }));
              }}
              value={state.billingCompany || ""}
              placeholder={I18n.eg_wapda}
              required
              handleStartLogEvent={() => {}}
              handleManuallyInput={() => {}}
              manuallyFocusInput={false}
            />
          </Box>
        ) : null}

        {state.billingCompany ? (
          <Box
            className={`${formStyles.tabAutoCompleteBox} tabAutoCompleteBox`}
          >
            <TabAutoComplete
              id="bill_payment_consumer"
              className={styles.consumerAutocomplete}
              list={consumerSuggestionList}
              listHeader={I18n.results}
              label={I18n.consumer_number}
              required
              handleSelect={(item) => {
                if (!item) {
                  cancelSearch();
                  setState((e) => ({ ...e, billingConsumerNumber: "" }));
                  return;
                }
                handleSuggestionSelect(item.raw || item);
              }}
              value={state.billingConsumerNumber || ""}
              placeholder={I18n.type_scan_account_number}
              hideSuffixIcon
              hideEmptySuggestions
              loading={isSearching}
              onInputChange={handleConsumerNumberChange}
              maxLength={72}
              onKeyDown={(e) => {
                // Handle Enter separately
                if (e.key === "Enter") {
                  e.preventDefault();

                  if (consumerSuggestions.length > 0) {
                    handleSuggestionSelect(consumerSuggestions[0]);
                  } else if (!isFormDisabled && onEnterPress) {
                    onEnterPress();
                  }
                  return;
                }

                // Allow control/navigation keys
                const allowedKeys = [
                  "Backspace",
                  "Delete",
                  "Tab",
                  "ArrowLeft",
                  "ArrowRight",
                  "Home",
                  "End",
                ];

                if (allowedKeys.includes(e.key)) return;

                // Allow digits only
                if (!/^[0-9]$/.test(e.key)) {
                  e.preventDefault();
                }
              }}
              handleStartLogEvent={() => {}}
              handleManuallyInput={() => {}}
              manuallyFocusInput={true}
            />
          </Box>
        ) : null}

        {state.billingCompany ? (
          !isSearching && consumerSuggestions.length === 0 ? (
            <Box className={styles.scanBarcodeRow}>
              <BarcodeIconNew width={14} height={14} />
              <ParagraphText
                className={`fontSize12 barclayBlueColor ${styles.scanBarcodeText}`}
              >
                {I18n.scan_barcode}
              </ParagraphText>
            </Box>
          ) : null
        ) : null}
      </Box>

      <SelectItemModal
        id="select_bill_modal"
        open={showAllBillsModal}
        onClose={() => setShowAllBillsModal(false)}
        onSelect={handleModalSelect}
        title={I18n.select_bill}
        items={allBillItems}
        searchPlaceholder={I18n.search_bill_by_name}
        noResultText={I18n.no_bills_found}
      />
    </Box>
  );
};

export default BillPaymentForm;
